/**
 * Combine multiple AbortSignals into a single signal that fires when any
 * source aborts. Prefers the native `AbortSignal.any` when available and
 * falls back to a manual proxy controller otherwise. Undefined inputs are
 * ignored. Returns `undefined` when no concrete signals are passed so callers
 * can keep their `signal?: AbortSignal` semantics intact.
 *
 * The fallback path detaches listeners from unfired source signals once any
 * one fires, so long-lived session signals don't accumulate listeners across
 * many tool calls.
 */
export function combineSignals(
  ...sources: Array<AbortSignal | undefined>
): AbortSignal | undefined {
  const signals = sources.filter((s): s is AbortSignal => s !== undefined);
  if (signals.length === 0) return undefined;
  if (signals.length === 1) return signals[0];
  // TODO: drop the fallback once min runtime guarantees `AbortSignal.any`
  // (Bun, Node ≥20.3, modern browsers all ship it).
  const anyFn = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any;
  if (typeof anyFn === 'function') return anyFn(signals);
  const controller = new AbortController();
  const onAny = () => {
    controller.abort();
    for (const s of signals) s.removeEventListener('abort', onAny);
  };
  for (const s of signals) {
    if (s.aborted) {
      controller.abort();
      return controller.signal;
    }
    s.addEventListener('abort', onAny, { once: true });
  }
  return controller.signal;
}
